'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Activity, Settings2, RefreshCw, AlertTriangle, CheckCircle2, ChevronRight, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SystemSimulationMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  limits?: { maxTurns?: number; maxActions?: number; maxReboots?: number; [key: string]: any };
  payload: {
    scenario?: string;
    maxTurns?: number;
    variables?: any[];
    controls?: any[];
    _hasHints?: boolean;
  };
}

export default function SystemSimulationMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, limits, payload }: SystemSimulationMechanicProps) {
  const actualMechanicId = mechanicId || id || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<Activity className="w-5 h-5" />}
      iconLabel="System Simulation — Control variables to reach target values"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <SystemSimulationMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} maxReboots={limits?.maxReboots} />
      )}
    </BaseMechanicShell>
  );
}

function SystemSimulationMechanicInner({ payload, submissionState, setAnswer, maxReboots }: any) {
  const scenario = payload?.scenario || "Stabilize the system before catastrophic failure.";
  const maxTurns = payload?.maxTurns || 10;
  const variables = payload?.variables || [];
  const controls = payload?.controls || [];

  const [turn, setTurn] = useState(0);
  const [currentVars, setCurrentVars] = useState<Record<string, number>>({});
  const [activeSwitches, setActiveSwitches] = useState<Record<string, boolean>>({});
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({});
  const [gameStatus, setGameStatus] = useState<"playing" | "won" | "lost">("playing");
  const [failReason, setFailReason] = useState<string | null>(null);
  const [rebootsLeft, setRebootsLeft] = useState(maxReboots || 0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { resetGame(); }, []);

  const resetGame = () => {
    const initial: Record<string, number> = {};
    const initSliders: Record<string, number> = {};
    const initSwitches: Record<string, boolean> = {};

    variables.forEach((v: any) => {
      initial[v.id] = v.initialValue;
    });

    controls.forEach((c: any) => {
      if (c.type === "slider") {
        // Initialize slider to the bound variable's initialValue so it doesn't
        // silently override the variable to 0 on the very first action.
        const boundVar = c.bindsTo ? variables.find((v: any) => v.id === c.bindsTo) : null;
        initSliders[c.id] = boundVar !== undefined && boundVar !== null
          ? (boundVar.initialValue ?? c.min ?? 0)
          : (c.min ?? 0);
      } else if (c.type === "switch") {
        initSwitches[c.id] = false;
      }
    });

    setCurrentVars(initial);
    setSliderValues(initSliders);
    setActiveSwitches(initSwitches);
    setTurn(0);
    setGameStatus("playing");
    setFailReason(null);
    setRebootsLeft(maxReboots || 0);
    setAnswer(null);
  };

  const doReboot = () => {
    const initial: Record<string, number> = {};
    const initSliders: Record<string, number> = {};
    const initSwitches: Record<string, boolean> = {};

    variables.forEach((v: any) => {
      initial[v.id] = v.initialValue;
    });

    controls.forEach((c: any) => {
      if (c.type === "slider") {
        const boundVar = c.bindsTo ? variables.find((v: any) => v.id === c.bindsTo) : null;
        initSliders[c.id] = boundVar !== undefined && boundVar !== null
          ? (boundVar.initialValue ?? c.min ?? 0)
          : (c.min ?? 0);
      } else if (c.type === "switch") {
        initSwitches[c.id] = false;
      }
    });

    setCurrentVars(initial);
    setSliderValues(initSliders);
    setActiveSwitches(initSwitches);
    // Turns are a global budget — reboots reset system state but NOT the clock.
    // If turns are already exhausted when reboot is called, go straight to GAME OVER.
    if (turn >= maxTurns) {
      setRebootsLeft(0); // force GAME OVER overlay
      setFailReason("Mission Failed: All turns exhausted — nothing left to reboot into.");
      setGameStatus("lost");
      return;
    }
    setRebootsLeft((prev: number) => Math.max(0, prev - 1));
    setGameStatus("playing");
    setFailReason(null);
    setAnswer(null);
  };

  const checkWinCondition = (vars: Record<string, number>, currentTurn: number) => {
    let hasTargets = false;
    let allMet = true;
    for (const v of variables) {
      if (v.targetValue !== undefined && v.targetValue !== null) {
        hasTargets = true;
        // "checks each variable with targetValue against tolerance" — overshooting
        // a target still counts as a win (e.g. Market Position hits 90 with target 80).
        if (vars[v.id] < v.targetValue) {
          allMet = false;
          break;
        }
      }
    }
    if (hasTargets && allMet) {
      setGameStatus("won");
      setAnswer({ finalState: vars, turns: currentTurn });
      return true;
    }
    return false;
  };

  // Returns: false (no fail) | 'boundary' (variable crash, rebootable) | 'turns' (turn limit, game over)
  const checkFailCondition = (vars: Record<string, number>, currentTurn: number): false | 'boundary' | 'turns' => {
    for (const v of variables) {
      if (v.min !== undefined && vars[v.id] < v.min) {
        setFailReason(`Critical Error: ${v.name} collapsed below minimum (${v.min}).`);
        setGameStatus("lost");
        return 'boundary';
      }
      if (v.max !== undefined && vars[v.id] > v.max) {
        setFailReason(`Critical Error: ${v.name} exceeded maximum capacity (${v.max}).`);
        setGameStatus("lost");
        return 'boundary';
      }
    }
    if (currentTurn >= maxTurns) {
      setFailReason("Mission Failed: All turns exhausted without reaching targets.");
      setGameStatus("lost");
      return 'turns';
    }
    return false;
  };

  const advanceTurn = (buttonEffects?: { variableId: string, delta: number }[]) => {
    if (gameStatus !== "playing") return;
    const nextVars = { ...currentVars };

    if (buttonEffects) {
      buttonEffects.forEach(eff => {
        nextVars[eff.variableId] = (nextVars[eff.variableId] || 0) + eff.delta;
      });
    }

    controls.filter((c: any) => c.type === "switch" && activeSwitches[c.id]).forEach((sw: any) => {
      (sw.activeEffects || []).forEach((eff: any) => {
        nextVars[eff.variableId] = (nextVars[eff.variableId] || 0) + eff.delta;
      });
    });

    // Slider direct-set only fires on "Advance Sequence" (no button effects).
    // When a button is clicked, its delta effects are authoritative — the slider
    // should not wipe them. The slider acts as a manual override for standalone turns.
    if (!buttonEffects) {
      controls.filter((c: any) => c.type === "slider" && c.bindsTo).forEach((sl: any) => {
        nextVars[sl.bindsTo] = sliderValues[sl.id];
      });
    }

    variables.forEach((v: any) => {
      if (v.perTurnDelta) {
        nextVars[v.id] += v.perTurnDelta;
      }
    });

    const nextTurn = turn + 1;
    setCurrentVars(nextVars);
    setTurn(nextTurn);

    const failKind = checkFailCondition(nextVars, nextTurn);
    if (failKind) return;
    checkWinCondition(nextVars, nextTurn);
  };

  const renderHardwareGauge = (v: any) => {
    const val = currentVars[v.id] || 0;
    const min = v.min !== undefined ? v.min : 0;
    const max = v.max !== undefined ? v.max : Math.max(100, val * 2);
    const range = max - min;
    const percentage = Math.min(100, Math.max(0, ((val - min) / range) * 100));
    
    let strokeColor = "text-emerald-400";
    if (v.max !== undefined && val > v.max * 0.8) strokeColor = "text-red-500";
    else if (v.min !== undefined && val < v.min + (range * 0.2)) strokeColor = "text-amber-500";
    else if (v.targetValue !== undefined && val === v.targetValue) strokeColor = "text-cyan-400";

    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    // We only want a 3/4 circle for the gauge (270 degrees)
    const arcLength = circumference * 0.75;
    const dashoffset = arcLength - (percentage / 100) * arcLength;

    return (
      <div key={v.id} className="relative flex flex-col items-center justify-center p-4 bg-zinc-900/80 rounded-2xl border border-zinc-700/50 shadow-[inset_0_2px_15px_rgba(0,0,0,0.5)] group">
        <div className="absolute top-2 left-2 w-1.5 h-1.5 rounded-full bg-zinc-800 shadow-inner" />
        <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-zinc-800 shadow-inner" />
        <div className="absolute bottom-2 left-2 w-1.5 h-1.5 rounded-full bg-zinc-800 shadow-inner" />
        <div className="absolute bottom-2 right-2 w-1.5 h-1.5 rounded-full bg-zinc-800 shadow-inner" />

        <div className="relative w-28 h-28 flex items-center justify-center">
          <svg className="w-full h-full transform -rotate-[135deg]" viewBox="0 0 100 100">
            {/* Background Track */}
            <circle cx="50" cy="50" r={radius} className="text-zinc-800" strokeWidth="8" stroke="currentColor" fill="transparent" strokeDasharray={circumference} strokeDashoffset={circumference * 0.25} strokeLinecap="round" />
            {/* Value Track */}
            <circle cx="50" cy="50" r={radius} className={`${strokeColor} transition-all duration-500 drop-shadow-[0_0_8px_currentColor]`} strokeWidth="8" stroke="currentColor" fill="transparent" strokeDasharray={circumference} strokeDashoffset={dashoffset + (circumference * 0.25)} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pt-2">
            <span className={`text-2xl font-mono font-black tracking-tighter ${strokeColor} drop-shadow-[0_0_5px_currentColor]`}>{val}</span>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{v.name}</div>
          {v.targetValue !== undefined && (
            <div className="text-[9px] font-mono text-cyan-500/70 mt-0.5">TGT: {v.targetValue}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-zinc-950 p-6 sm:p-8 rounded-3xl border-[6px] border-zinc-800 shadow-2xl relative overflow-hidden text-zinc-300 font-sans select-none">
      {/* Decorative Hardware Elements */}
      <div className="absolute top-4 left-4 w-3 h-3 rounded-full bg-zinc-700 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]" />
      <div className="absolute top-4 right-4 w-3 h-3 rounded-full bg-zinc-700 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]" />
      <div className="absolute bottom-4 left-4 w-3 h-3 rounded-full bg-zinc-700 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]" />
      <div className="absolute bottom-4 right-4 w-3 h-3 rounded-full bg-zinc-700 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-2 bg-yellow-500/80 rounded-b-xl shadow-[0_0_10px_rgba(234,179,8,0.3)]" />

      {scenario && (
        <div className="mb-8 border border-cyan-900/50 bg-cyan-950/20 p-4 rounded-xl flex gap-4 items-start shadow-[inset_0_0_20px_rgba(8,145,178,0.1)]">
          <Activity className="w-6 h-6 text-cyan-500 shrink-0 mt-0.5 animate-pulse" />
          <div>
            <div className="text-xs font-bold text-cyan-500 tracking-widest uppercase mb-1">Mission Briefing</div>
            <div className="text-sm text-cyan-100/80 leading-relaxed">{scenario}</div>
          </div>
        </div>
      )}

      {/* Main Control Board */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left: Telemetry Array */}
        <div className="lg:col-span-7 bg-zinc-900 p-6 rounded-2xl border border-zinc-800 shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-800/50">
            <h4 className="text-xs uppercase font-bold text-zinc-500 tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Main Telemetry
            </h4>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">Cycles Left</span>
              <div className={`font-mono text-xl font-black bg-black px-3 py-1 rounded-md border ${maxTurns - turn <= 3 ? 'text-red-500 border-red-900/50 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]' : 'text-emerald-500 border-emerald-900/50'}`}>
                {(maxTurns - turn).toString().padStart(2, '0')}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {variables.map(renderHardwareGauge)}
          </div>
        </div>

        {/* Right: Operations Deck */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          <h4 className="text-xs uppercase font-bold text-zinc-500 tracking-widest flex items-center gap-2 ml-2">
            <Settings2 className="w-4 h-4" />
            Operations Deck
          </h4>
          
          <div className="flex-1 bg-zinc-900/50 p-5 rounded-2xl border border-zinc-800 flex flex-col gap-5 shadow-inner">
            {controls.map((c: any) => {
              
              if (c.type === "button") {
                return (
                  <div key={c.id} className="relative group">
                    <button 
                      type="button"
                      disabled={gameStatus !== "playing"}
                      onClick={() => advanceTurn(c.effects)}
                      className="w-full flex items-center justify-between bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-900 p-4 rounded-xl border-b-4 border-zinc-950 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50 disabled:pointer-events-none group-hover:shadow-[0_0_15px_rgba(255,255,255,0.05)]"
                    >
                      <span className="text-sm font-bold tracking-wider text-zinc-200 uppercase">{c.label}</span>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-[inset_0_2px_4px_rgba(255,255,255,0.3),0_2px_10px_rgba(239,68,68,0.4)] group-active:shadow-[inset_0_4px_8px_rgba(0,0,0,0.5)] border border-red-900">
                        <Zap className="w-5 h-5 text-white drop-shadow-md" />
                      </div>
                    </button>
                  </div>
                );
              }
              
              if (c.type === "switch") {
                const isActive = activeSwitches[c.id];
                return (
                  <div key={c.id} className="flex items-center justify-between bg-zinc-950/80 p-4 rounded-xl border border-zinc-800 shadow-inner">
                    <div className="text-sm font-bold tracking-wider text-zinc-400 uppercase">{c.label}</div>
                    <button
                      type="button"
                      disabled={gameStatus !== "playing"}
                      onClick={() => setActiveSwitches(prev => ({...prev, [c.id]: !prev[c.id]}))}
                      className={`relative w-16 h-8 rounded-full transition-colors duration-300 border-2 ${isActive ? 'bg-cyan-900 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'bg-zinc-800 border-zinc-700'}`}
                    >
                      <div className={`absolute top-0.5 w-6 h-6 rounded-full transition-transform duration-300 flex items-center justify-center ${isActive ? 'translate-x-8 bg-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.8)]' : 'translate-x-0.5 bg-zinc-500'}`}>
                         {isActive && <div className="w-2 h-2 rounded-full bg-white animate-pulse" />}
                      </div>
                    </button>
                  </div>
                );
              }
              
              if (c.type === "slider") {
                return (
                  <div key={c.id} className="bg-zinc-950/80 p-5 rounded-xl border border-zinc-800 shadow-inner flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-bold tracking-wider text-zinc-400 uppercase">{c.label}</div>
                      <div className="text-lg font-mono font-black text-amber-500 drop-shadow-[0_0_5px_rgba(245,158,11,0.5)]">
                        {sliderValues[c.id]}
                      </div>
                    </div>
                    {/* Mechanical Fader Style */}
                    <div className="relative h-6 flex items-center">
                      <div className="absolute inset-0 h-2 my-auto bg-black rounded-full shadow-[inset_0_1px_3px_rgba(0,0,0,0.8)] border border-zinc-800" />
                      <input 
                        type="range"
                        min={c.min || 0}
                        max={c.max || 100}
                        step={c.step || 1}
                        disabled={gameStatus !== "playing"}
                        value={sliderValues[c.id]}
                        onChange={(e) => setSliderValues(prev => ({...prev, [c.id]: parseFloat(e.target.value)}))}
                        className="w-full relative z-10 opacity-0 cursor-pointer h-full"
                      />
                      <div 
                        className="absolute h-8 w-5 bg-gradient-to-b from-zinc-300 to-zinc-500 rounded border-2 border-zinc-700 shadow-md pointer-events-none -ml-2.5 flex items-center justify-center"
                        style={{ 
                          left: `${((sliderValues[c.id] - (c.min || 0)) / ((c.max || 100) - (c.min || 0))) * 100}%`,
                          top: '-4px'
                        }}
                      >
                        <div className="w-full h-0.5 bg-zinc-800 shadow-[0_1px_0_rgba(255,255,255,0.3)]" />
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>

          {controls.some((c: any) => c.type !== "button") && (
            <button 
              type="button"
              disabled={gameStatus !== "playing"}
              onClick={() => advanceTurn()}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold uppercase tracking-widest p-4 rounded-xl border border-zinc-600 flex items-center justify-center gap-2 active:bg-zinc-900 transition-colors disabled:opacity-50"
            >
              Advance Sequence <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* SYSTEM FAILURE — variable boundary crash, player has reboots AND turns remaining */}
      {gameStatus === "lost" && rebootsLeft > 0 && turn < maxTurns && (
        <div className="absolute inset-0 z-50 bg-red-950/90 backdrop-blur-sm flex flex-col items-center justify-center p-6 border-8 border-red-600/50">
          <div className="w-full max-w-md bg-black border border-red-500 p-8 rounded-2xl shadow-[0_0_50px_rgba(220,38,38,0.3)] text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagonal-stripes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4 drop-shadow-[0_0_15px_currentColor]" />
            <div className="text-3xl font-black text-red-500 tracking-tighter mb-2 drop-shadow-[0_0_10px_currentColor]">SYSTEM FAILURE</div>
            <div className="text-red-400 font-mono text-sm mb-4 bg-red-950/50 p-3 rounded">{failReason}</div>
            <div className="text-amber-400 font-mono text-xs mb-6">Reboots remaining: {rebootsLeft} &nbsp;·&nbsp; Turns remaining: {maxTurns - turn}</div>
            <button type="button" onClick={doReboot} className="bg-amber-900 hover:bg-amber-800 text-white px-8 py-3 rounded-full font-bold uppercase tracking-widest flex items-center gap-2 mx-auto transition-colors border border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.5)]">
              <RefreshCw className="w-5 h-5" /> Reboot Sequence
            </button>
          </div>
        </div>
      )}
      {/* GAME OVER — no reboots left, OR turns exhausted (reboots can't help with 0 turns) */}
      {gameStatus === "lost" && (rebootsLeft <= 0 || turn >= maxTurns) && (
        <div className="absolute inset-0 z-50 bg-red-950/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 border-8 border-red-600/50">
          <div className="w-full max-w-md bg-black border border-red-500 p-8 rounded-2xl shadow-[0_0_50px_rgba(220,38,38,0.3)] text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagonal-stripes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4 drop-shadow-[0_0_15px_currentColor]" />
            <div className="text-3xl font-black text-red-500 tracking-tighter mb-2 drop-shadow-[0_0_10px_currentColor]">GAME OVER</div>
            <div className="text-red-400 font-mono text-sm mb-8 bg-red-950/50 p-3 rounded">{failReason}</div>
          </div>
        </div>
      )}

      {gameStatus === "won" && (
        <div className="absolute inset-0 z-50 bg-cyan-950/90 backdrop-blur-sm flex flex-col items-center justify-center p-6 border-8 border-cyan-500/30">
          <div className="w-full max-w-md bg-black border border-cyan-500 p-8 rounded-2xl shadow-[0_0_50px_rgba(6,182,212,0.3)] text-center">
            <CheckCircle2 className="w-16 h-16 text-cyan-400 mx-auto mb-4 drop-shadow-[0_0_15px_currentColor]" />
            <div className="text-3xl font-black text-cyan-400 tracking-tighter mb-2 drop-shadow-[0_0_10px_currentColor]">STABILIZATION ACHIEVED</div>
            <div className="text-cyan-600 font-mono text-sm mb-8">All parameters within optimal targets.</div>
          </div>
        </div>
      )}
    </div>
  );
}
